<script setup>
import { ref, watch, onMounted } from 'vue';
import {
  useFunctionGetter,
  useMapGetter,
  useStore,
} from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import NewHook from './NewHook.vue';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';

const store = useStore();
const { t } = useI18n();
const frenetLogoUrl = '/dashboard/images/integrations/frenet.svg';
const integration = useFunctionGetter('integrations/getIntegration', 'frenet');
const uiFlags = useMapGetter('integrations/getUIFlags');

const showAddHookModal = ref(false);
const showDeleteConfirmationPopup = ref(false);
const selectedHook = ref({});
const sellerCep = ref('');
const cepMessage = ref('');
const cepMessageType = ref('info');

const frenetHook = () => integration.value?.hooks?.[0];

watch(
  () => integration.value?.hooks?.[0],
  hook => {
    if (hook?.settings?.seller_cep) {
      sellerCep.value = hook.settings.seller_cep;
    }
  },
  { immediate: true }
);

const openAddHookModal = () => {
  showAddHookModal.value = true;
};
const hideAddHookModal = async () => {
  showAddHookModal.value = false;
  await store.dispatch('integrations/get');
};

const openDeletePopup = () => {
  selectedHook.value = frenetHook();
  showDeleteConfirmationPopup.value = true;
};
const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const confirmDeletion = async () => {
  try {
    await store.dispatch('integrations/deleteHook', {
      hookId: selectedHook.value.id,
      appId: 'frenet',
    });
    await store.dispatch('integrations/get');
    useAlert(t('INTEGRATION_APPS.DELETE.API.SUCCESS_MESSAGE'));
  } catch {
    useAlert(t('INTEGRATION_APPS.DELETE.API.ERROR_MESSAGE'));
  } finally {
    closeDeletePopup();
  }
};

const saveCep = async () => {
  const hook = frenetHook();
  if (!hook) return;
  try {
    await store.dispatch('integrations/updateHook', {
      hookId: hook.id,
      hookData: {
        settings: { ...hook.settings, seller_cep: sellerCep.value.trim() },
      },
    });
    cepMessage.value = t('INTEGRATION_SETTINGS.FRENET.SELLER_CEP.SUCCESS');
    cepMessageType.value = 'success';
  } catch {
    cepMessage.value = t('INTEGRATION_SETTINGS.FRENET.SELLER_CEP.ERROR');
    cepMessageType.value = 'error';
  }
};

onMounted(() => {
  store.dispatch('integrations/get');
});
</script>

<template>
  <SettingsLayout :is-loading="uiFlags.isFetching">
    <template #header>
      <BaseSettingsHeader
        :title="$t('INTEGRATION_SETTINGS.FRENET.HEADER')"
        description=""
        feature-name="frenet"
        :back-button-label="$t('INTEGRATION_SETTINGS.HEADER')"
      />
    </template>
    <template #body>
      <div class="flex flex-col gap-6">
        <!-- Connect / Disconnect card -->
        <div
          class="flex flex-col items-start justify-between lg:flex-row lg:items-center p-6 outline outline-n-container outline-1 bg-n-card rounded-xl gap-6"
        >
          <div
            class="flex items-start lg:items-center justify-start flex-1 m-0 gap-6 flex-col lg:flex-row"
          >
            <div
              class="flex h-16 w-16 items-center justify-center flex-shrink-0"
            >
              <img
                :src="frenetLogoUrl"
                class="max-w-full rounded-md border border-n-weak shadow-sm"
              />
            </div>
            <div>
              <h3 class="mb-1 text-heading-1 text-n-slate-12">
                {{ integration.name }}
              </h3>
              <p class="text-n-slate-11 text-body-main">
                {{ integration.description }}
              </p>
            </div>
          </div>
          <div class="flex justify-center items-center mb-0">
            <Button
              v-if="integration.enabled"
              ruby
              faded
              :label="$t('INTEGRATION_APPS.DISCONNECT.BUTTON_TEXT')"
              @click="openDeletePopup"
            />
            <Button
              v-else
              teal
              :label="$t('INTEGRATION_SETTINGS.CONNECT.BUTTON_TEXT')"
              @click="openAddHookModal"
            />
          </div>
        </div>

        <!-- CEP card — shown after connecting -->
        <div
          v-if="integration.enabled"
          class="flex flex-col gap-4 p-6 bg-n-solid-1 border border-n-weak rounded-lg"
        >
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ $t('INTEGRATION_SETTINGS.FRENET.SELLER_CEP.TITLE') }}
          </h3>
          <div class="flex items-start gap-3">
            <div class="flex-1">
              <Input
                v-model="sellerCep"
                :label="$t('INTEGRATION_SETTINGS.FRENET.SELLER_CEP.LABEL')"
                :placeholder="
                  $t('INTEGRATION_SETTINGS.FRENET.SELLER_CEP.PLACEHOLDER')
                "
                :message="
                  cepMessage ||
                  $t('INTEGRATION_SETTINGS.FRENET.SELLER_CEP.HELP')
                "
                :message-type="cepMessage ? cepMessageType : 'info'"
              />
            </div>
            <Button
              teal
              :label="$t('INTEGRATION_SETTINGS.FRENET.SELLER_CEP.SAVE')"
              :is-loading="uiFlags.isUpdatingHook"
              :disabled="!sellerCep.trim()"
              class="mt-6"
              @click="saveCep"
            />
          </div>
        </div>
      </div>
    </template>
  </SettingsLayout>

  <woot-modal v-model:show="showAddHookModal" :on-close="hideAddHookModal">
    <NewHook integration-id="frenet" @close="hideAddHookModal" />
  </woot-modal>

  <woot-delete-modal
    v-model:show="showDeleteConfirmationPopup"
    :on-close="closeDeletePopup"
    :on-confirm="confirmDeletion"
    :title="$t('INTEGRATION_APPS.DELETE.TITLE.ACCOUNT')"
    :message="$t('INTEGRATION_APPS.DELETE.MESSAGE.ACCOUNT')"
    :confirm-text="$t('INTEGRATION_APPS.DELETE.CONFIRM_BUTTON_TEXT.ACCOUNT')"
    :reject-text="$t('INTEGRATION_APPS.DELETE.CANCEL_BUTTON_TEXT')"
  />
</template>
